/* type BooksStore = {
    books: {
        cs: BookItem[]
        en: BookItem[]
    }
    bookTrees: {
        cs: Record<string, BookTreeItem[]>
        en: Record<string, BookTreeItem[]>
    }
    temaData: {
        cs: Record<string, TemaItem>
        en: Record<string, TemaItem>
    }
    // figuryMetadata: Record<string, ImageInfo>
    isFetching: boolean
} */

type Books = {
    cs: BookItem[]
    en: BookItem[]
}

type BookTrees = {
    cs: Record<string, BookTreeItem[]>
    en: Record<string, BookTreeItem[]>
}

type TemaData = {
    cs: Record<string, TemaItem>
    en: Record<string, TemaItem>
}

const BOOKS_URL = "seznamknih/"
const BOOK_TREE_BASE_URL = "booktree/"
const TEMA_BASE_URL = "tema"

const BASE_DOWNLOAD_URL =
    "https://app.iprpraha.cz/apl/mnt/App_UAP/portal/docs/2024"
const BOOK_FILE_NAMES: { [k: string]: string }[] = [
    { "000": "UAPo2024_0000_Mapa_obsahu_web.pdf" },
    { "050": "UAPo2024_0050_Metropole_a_region_web.pdf" },
    { "100": "UAPo2024_0100_Krajina_web.pdf" },
    { "200": "UAPo2024_0200_Mesto_web.pdf" },
    { "300": "UAPo2024_0300_Vyuziti_uzemi_web.pdf" },
    { "400": "UAPo2024_0400_Ekonomika_a_potencial_web.pdf" },
    { "500": "UAPo2024_0500_Zelena_infrastruktura_web.pdf" },
    { "600": "UAPo2024_0600_Dopravni_infrastruktura_web.pdf" },
    { "700": "UAPo2024_0700_Technicka_infrastruktura_web.pdf" },
    { "800": "UAPo2024_0800_Verejna_vybavenost_web.pdf" },
    { "900": "UAPo2024_0900_Hodnoty_a_limity_web.pdf" },
    { "1000": "UAPo2024_1000_Plany_a_zamery_v_uzemi_web.pdf" },
    { "1100": "UAPo2024_1100_Rozbor_udrzitelneho_rozvoje_web.pdf" },
    { K: "UAPk2024_UAP_kraje_textova_cast_web.pdf" },
]

export const useBooksStore = defineStore("books", () => {
    const mainStore = useMainStore()

    const books = ref<Books>({
        cs: [],
        en: [],
    })
    const bookTrees = ref<BookTrees>({
        cs: {},
        en: {},
    })
    const temaData = ref<TemaData>({
        cs: {},
        en: {},
    })
    const isFetching = ref(false)

    const allBookDownloadFiles = computed(() => BOOK_FILE_NAMES)

    const fetchBooks = async () => {
        try {
            const data = await Promise.all([
                $fetch<ResponseBookItem[]>(BOOKS_URL + "cz", {
                    baseURL: import.meta.client
                        ? mainStore.apiBaseUrl
                        : mainStore.apiBaseUrlInt,
                }),
                $fetch<ResponseBookItem[]>(BOOKS_URL + "en", {
                    baseURL: import.meta.client
                        ? mainStore.apiBaseUrl
                        : mainStore.apiBaseUrlInt,
                }),
            ])
            books.value.cs = data[0]
                .map(book => {
                    const kod = book.kniha
                    const title = book.nazev.split("|")[0] as string
                    const subtitle = book.nazev.split("|")[1] as string
                    return {
                        kod,
                        title,
                        subtitle,
                    }
                })
                .sort((a, b) => parseInt(a.kod) - parseInt(b.kod))
            books.value.en = data[1]
                .map(book => {
                    let kod, title, subtitle
                    if (book.nazev.includes("kraj")) {
                        kod = "K"
                        title = book.nazev
                        subtitle = book.nazev
                    } else {
                        kod = book.kniha
                        title = book.nazev.split("|")[0] as string
                        subtitle = book.nazev.split("|")[1] as string
                    }
                    return {
                        kod,
                        title,
                        subtitle,
                    }
                })
                .sort((a, b) => parseInt(a.kod) - parseInt(b.kod))
            return books.value
        } catch (error) {
            console.warn("Error fetching books", error)
            return null
        }
    }

    const fetchBookTree = async (lang: "cs" | "en" = "cs", bookNo: string) => {
        if (bookTrees.value[lang][bookNo]) return bookTrees.value[lang][bookNo]
        isFetching.value = true
        const url = `${BOOK_TREE_BASE_URL}${bookNo}/${lang === "cs" ? "cz" : "en"}`
        try {
            const data = await $fetch<BookTree>(url, {
                baseURL: import.meta.client
                    ? mainStore.apiBaseUrl
                    : mainStore.apiBaseUrlInt,
            })
            bookTrees.value[lang][bookNo] = data.tree
            return bookTrees.value[lang][bookNo]
        } catch (error) {
            console.warn("Error fetching book tree " + bookNo, error)
            return null
        } finally {
            isFetching.value = false
        }
    }

    const fetchTemaData = async (
        lang: "cs" | "en" = "cs",
        temaKod: string,
        bookKod: string
    ) => {
        const kod = `${bookKod}.${temaKod}`
        if (temaData.value[lang][kod]) return temaData.value[lang][kod]
        isFetching.value = true
        const url = `${TEMA_BASE_URL}/${kod}/${lang === "cs" ? "cz" : "en"}`
        try {
            const data = await $fetch<TemaItem[]>(url, {
                baseURL: import.meta.client
                    ? mainStore.apiBaseUrl
                    : mainStore.apiBaseUrlInt,
            })
            if (data.length === 0) {
                // po kliku na podkapitolu nebo kapitolu se zkusí dotaz na první téma
                let bookTree = bookTrees.value[lang][bookKod]
                if (!bookTree) {
                    await fetchBookTree(lang, bookKod)
                    bookTree = bookTrees.value[lang][bookKod]
                }
                if (!bookTree) {
                    console.warn("No book tree data for book " + bookKod)
                    return null
                }
                const idx = bookTree.findIndex(item => item.kod === temaKod)
                if (idx === -1) {
                    console.warn("No data for tema " + kod)
                    return null
                }
                const nextTema = bookTree
                    .slice(idx + 1)
                    .find(item => item.style === "uap04")
                if (!nextTema) {
                    console.warn("No data for tema " + kod)
                    return null
                }
                const nextTemaKod = `${bookKod}.${nextTema.kod}`
                if (temaData.value[lang][nextTemaKod])
                    return temaData.value[lang][nextTemaKod]
                const url2 = `${TEMA_BASE_URL}/${nextTemaKod}/${lang === "cs" ? "cz" : "en"}`
                const data2 = await $fetch<TemaItem[]>(url2, {
                    baseURL: import.meta.client
                        ? mainStore.apiBaseUrl
                        : mainStore.apiBaseUrlInt,
                })
                if (data2.length === 0) {
                    console.warn("No data for tema " + kod)
                    return null
                } else {
                    const metadata = data2[0] as TemaItem
                    temaData.value[lang][metadata.KOD] = metadata
                    return temaData.value[lang][metadata.KOD]
                }
            } else {
                const metadata = data[0] as TemaItem
                temaData.value[lang][metadata.KOD] = metadata
                return temaData.value[lang][metadata.KOD]
            }
        } catch (error) {
            console.warn("Error fetching tema data " + kod, error)
            return null
        } finally {
            isFetching.value = false
        }
    }

    const getBookFilePath = (bookKod: string) => {
        const fileObj = BOOK_FILE_NAMES.find(item => item[bookKod])
        const obecOrKraj = bookKod === "K" ? "kraj" : "obec"
        if (!fileObj) return ""
        const fileName = fileObj[bookKod]
        return `${BASE_DOWNLOAD_URL}/${obecOrKraj}/textova_cast/${fileName}`
    }

    return {
        books,
        bookTrees,
        temaData,
        isFetching,
        allBookDownloadFiles,
        fetchBooks,
        fetchBookTree,
        fetchTemaData,
        getBookFilePath,
    }
})

/* const useBooksStore = defineStore("books", {
    state: (): BooksStore => {
        return {
            books: {
                cs: [],
                en: [],
            },
            bookTrees: {
                cs: {},
                en: {},
            },
            temaData: {
                cs: {},
                en: {},
            },
            // figuryMetadata: {},
            isFetching: false,
        }
    },
    getters: {
        allBookDownloadFiles() {
            return BOOK_FILE_NAMES
        },
    },
    actions: {
        async fetchBooks() {
            const mainStore = useMainStore()
            try {
                const data = await Promise.all([
                    $fetch<ResponseBookItem[]>(BOOKS_URL + "cz", {
                        baseURL: import.meta.client
                            ? mainStore.apiBaseUrl
                            : mainStore.apiBaseUrlInt,
                    }),
                    $fetch<ResponseBookItem[]>(BOOKS_URL + "en", {
                        baseURL: import.meta.client
                            ? mainStore.apiBaseUrl
                            : mainStore.apiBaseUrlInt,
                    }),
                ])
                this.books.cs = data[0]
                    .map(book => {
                        const kod = book.kniha
                        const title = book.nazev.split("|")[0] as string
                        const subtitle = book.nazev.split("|")[1] as string
                        return {
                            kod,
                            title,
                            subtitle,
                        }
                    })
                    .sort((a, b) => parseInt(a.kod) - parseInt(b.kod))
                this.books.en = data[1]
                    .map(book => {
                        let kod, title, subtitle
                        if (book.nazev.includes("kraj")) {
                            kod = "K"
                            title = book.nazev
                            subtitle = book.nazev
                        } else {
                            kod = book.kniha
                            title = book.nazev.split("|")[0] as string
                            subtitle = book.nazev.split("|")[1] as string
                        }
                        return {
                            kod,
                            title,
                            subtitle,
                        }
                    })
                    .sort((a, b) => parseInt(a.kod) - parseInt(b.kod))
                return this.books
            } catch (error) {
                console.warn("Error fetching books", error)
                return null
            }
        },
        async fetchBookTree(lang: "cs" | "en" = "cs", bookNo: string) {
            if (this.bookTrees[lang][bookNo])
                return this.bookTrees[lang][bookNo]
            const mainStore = useMainStore()
            this.isFetching = true
            const url = `${BOOK_TREE_BASE_URL}${bookNo}/${lang === "cs" ? "cz" : "en"}`
            try {
                const data = await $fetch<BookTree>(url, {
                    baseURL: import.meta.client
                        ? mainStore.apiBaseUrl
                        : mainStore.apiBaseUrlInt,
                })
                this.bookTrees[lang][bookNo] = data.tree
                return this.bookTrees[lang][bookNo]
            } catch (error) {
                console.warn("Error fetching book tree " + bookNo, error)
                return null
            } finally {
                this.isFetching = false
            }
        },
        async fetchTemaData(
            lang: "cs" | "en" = "cs",
            temaKod: string,
            bookKod: string
        ) {
            const kod = `${bookKod}.${temaKod}`
            if (this.temaData[lang][kod]) return this.temaData[lang][kod]
            const mainStore = useMainStore()
            this.isFetching = true
            const url = `${TEMA_BASE_URL}/${kod}/${lang === "cs" ? "cz" : "en"}`
            try {
                const data = await $fetch<TemaItem[]>(url, {
                    baseURL: import.meta.client
                        ? mainStore.apiBaseUrl
                        : mainStore.apiBaseUrlInt,
                })
                if (data.length === 0) {
                    // po kliku na podkapitolu nebo kapitolu se zkusí dotaz na první téma
                    let bookTree = this.bookTrees[lang][bookKod]
                    if (!bookTree) {
                        await this.fetchBookTree(lang, bookKod)
                        bookTree = this.bookTrees[lang][bookKod]
                    }
                    if (!bookTree) {
                        console.warn("No book tree data for book " + bookKod)
                        return null
                    }
                    const idx = bookTree.findIndex(item => item.kod === temaKod)
                    if (idx === -1) {
                        console.warn("No data for tema " + kod)
                        return null
                    }
                    const nextTema = bookTree
                        .slice(idx + 1)
                        .find(item => item.style === "uap04")
                    if (!nextTema) {
                        console.warn("No data for tema " + kod)
                        return null
                    }
                    const nextTemaKod = `${bookKod}.${nextTema.kod}`
                    if (this.temaData[lang][nextTemaKod])
                        return this.temaData[lang][nextTemaKod]
                    const url2 = `${TEMA_BASE_URL}/${nextTemaKod}/${lang === "cs" ? "cz" : "en"}`
                    const data2 = await $fetch<TemaItem[]>(url2, {
                        baseURL: import.meta.client
                            ? mainStore.apiBaseUrl
                            : mainStore.apiBaseUrlInt,
                    })
                    if (data2.length === 0) {
                        console.warn("No data for tema " + kod)
                        return null
                    } else {
                        const metadata = data2[0] as TemaItem
                        this.temaData[lang][metadata.KOD] = metadata
                        return this.temaData[lang][metadata.KOD]
                    }
                } else {
                    const metadata = data[0] as TemaItem
                    this.temaData[lang][metadata.KOD] = metadata
                    return this.temaData[lang][metadata.KOD]
                }
            } catch (error) {
                console.warn("Error fetching tema data " + kod, error)
                return null
            } finally {
                this.isFetching = false
            }
        },
        getBookFilePath(bookKod: string) {
            const fileObj = BOOK_FILE_NAMES.find(item => item[bookKod])
            if (!fileObj) return ""
            const fileName = fileObj[bookKod]
            return `${BASE_DOWNLOAD_URL}${fileName}`
        },
    },
}) */

if ((import.meta as MetaHot).hot) {
    ;(import.meta as MetaHot).hot?.accept(
        acceptHMRUpdate(useBooksStore, (import.meta as MetaHot).hot)
    )
}
